import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookIcon, HeadphonesIcon, TrophyIcon, CalendarIcon, ThumbsUpIcon, BookOpenIcon, BriefcaseIcon, HeartIcon, StarIcon, BellIcon, PenToolIcon, WrenchIcon } from 'lucide-react';
import ArticleCard from '../components/Articles/ArticleCard';
import TroveFeatureCard from '../components/TroveFeatureCard';
import { TroveActivityFeed } from '../components/Trove/TroveActivityFeed';
import { ActivityPost } from '../components/Trove/ActivityPostCard';
import { useUserRole } from '../context/UserRoleContext';
import { SearchWithAutocomplete } from '../components/UI/SearchWithAutocomplete';
import { TroveLayout } from '../components/Trove/TroveLayout';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { useDarkClasses } from '../hooks/useDarkClasses';
// Reader-focused activity posts
const readerActivityPosts: ActivityPost[] = [{
  id: 1,
  type: 'New Release',
  title: 'Iron Flame',
  description: 'Rebecca Yarros continues the Empyrean series in this stunning sequel.',
  author: 'Rebecca Yarros',
  date: 'Oct 15, 2023',
  image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
}, {
  id: 2,
  type: 'Cover Reveal',
  title: 'The Familiar — Leigh Bardugo',
  description: "First look at the cover for Leigh Bardugo's upcoming standalone fantasy novel.",
  author: 'Leigh Bardugo',
  date: 'Oct 14, 2023'
}, {
  id: 3,
  type: 'Audiobook Launch',
  title: 'Happy Place — Full Cast Audio',
  description: "Emily Henry's beloved romance now available as a full cast audiobook.",
  author: 'Emily Henry',
  date: 'Oct 13, 2023'
}, {
  id: 4,
  type: 'New Release',
  title: 'Holly',
  description: "Stephen King's beloved character Holly Gibney returns in a new standalone thriller.",
  author: 'Stephen King',
  date: 'Oct 12, 2023',
  image: 'https://images.unsplash.com/photo-1551269901-5c5e14c25df7?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
}, {
  id: 5,
  type: 'Sale',
  title: 'Indie Romance Bundle — 10 Books',
  description: 'Ten bestselling indie romance novels at a special bundle price for this week only.',
  author: 'Various Authors',
  date: 'Oct 11, 2023'
}];
// Vendor-focused activity posts
const vendorActivityPosts: ActivityPost[] = [{
  id: 101,
  type: 'Vendor Availability',
  title: 'Romance Cover Designer — Open for Q1 2024',
  description: 'Specialising in contemporary and paranormal romance. 5 slots available. DM for rates.',
  author: 'Luna Design Co.',
  date: 'Oct 15, 2023'
}, {
  id: 102,
  type: 'Collaboration Request',
  title: 'Co-Author Wanted — Fantasy Series',
  description: 'Established fantasy author seeking co-writer for a new 5-book epic fantasy series.',
  author: 'D. Morgan',
  date: 'Oct 14, 2023'
}, {
  id: 103,
  type: 'Vendor Availability',
  title: 'Developmental Editor — Taking New Clients',
  description: 'Specialising in romance and fantasy. 10+ years experience. Free 30-min consultation.',
  author: 'Wordsmith Editorial',
  date: 'Oct 13, 2023'
}, {
  id: 104,
  type: 'Collaboration Request',
  title: 'ARC Readers Needed — Cozy Mystery',
  description: 'Debut author seeking 20 ARC readers for a cozy mystery series. Feedback required within 3 weeks.',
  author: 'Claire MacAllister',
  date: 'Oct 12, 2023'
}, {
  id: 105,
  type: 'Award Nomination',
  title: 'Indie Lit Awards — Submission Deadline Nov 30',
  description: 'Submit your work for consideration across 12 categories. All indie authors eligible.',
  author: 'Indie Lit Awards',
  date: 'Oct 11, 2023'
}];
const featuredArticles = [{
  id: 1,
  title: 'Character Relationships Are Representation, Not Trigger Warnings',
  excerpt: 'A response to recent discussions about the labeling of LGBTQ+ and non-traditional relationships in romance literature...',
  author: 'Dr. Sarah Chen',
  date: 'October 15, 2023',
  category: 'Industry Insights',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1467991521834-fb8e202c7074?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}];
const romanceArticles = [{
  id: 1,
  title: 'The Evolution of Romance Tropes in 2023',
  excerpt: 'How classic romance tropes are being reimagined for modern readers...',
  author: 'Emma Clarke',
  date: 'October 9, 2023',
  category: 'Romance',
  readTime: '7 min read',
  image: 'https://images.unsplash.com/photo-1474552226712-ac0f0961a954?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 2,
  title: 'Diversity in Romance: Beyond the Mainstream',
  excerpt: 'Exploring the growing diversity in romance fiction and its impact...',
  author: 'Priya Sharma',
  date: 'October 6, 2023',
  category: 'Romance',
  readTime: '9 min read',
  image: 'https://images.unsplash.com/photo-1516575334481-f85287c2c82d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}];
const awardCategories = [{
  id: 1,
  title: 'Best Narrator',
  description: 'Celebrating exceptional audiobook performances',
  nominees: [{
    id: 1,
    name: 'Sarah Johnson',
    work: 'The Silent Echo',
    votes: 1247
  }, {
    id: 2,
    name: 'Michael Harris',
    work: 'Beyond the Veil',
    votes: 1103
  }, {
    id: 3,
    name: 'Elena Martinez',
    work: 'Whispers in the Dark',
    votes: 986
  }]
}, {
  id: 2,
  title: 'Debut Author of the Year',
  description: 'Recognizing outstanding first-time authors',
  nominees: [{
    id: 1,
    name: 'Aiden Park',
    work: 'The Memory Collector',
    votes: 876
  }, {
    id: 2,
    name: 'Zoe Chen',
    work: 'Fractured Realms',
    votes: 812
  }, {
    id: 3,
    name: 'Lucas Rodriguez',
    work: 'Beneath the Surface',
    votes: 754
  }]
}, {
  id: 3,
  title: 'Book of the Year',
  description: 'The most exceptional indie book of 2023',
  nominees: [{
    id: 1,
    name: 'The Silent Echo',
    author: 'Elena Richards',
    votes: 2341
  }, {
    id: 2,
    name: 'Beyond the Veil',
    author: 'Marcus Chen',
    votes: 2187
  }, {
    id: 3,
    name: 'Whispers in the Dark',
    author: 'Sarah Johnson',
    votes: 1956
  }]
}];
const Trove = () => {
  const {
    isReader
  } = useUserRole();
  const rt = useRoleTheme();
  const dk = useDarkClasses();
  const [searchQuery, setSearchQuery] = useState('');
  const activityPosts = isReader ? readerActivityPosts : vendorActivityPosts;
  const activityTitle = isReader ? 'Latest Updates' : 'Industry Updates';
  const troveSections = [{
    title: 'Updates',
    description: 'New releases, cover reveals, and the latest indie publishing news.',
    icon: <BellIcon className={`h-6 w-6 ${rt.textPrimary}`} data-id="element-15726" data-mp="1erhhe7wynr6i" />,
    linkTo: '/trove/updates',
    bgColor: dk.isDark ? 'bg-gray-800' : 'bg-slate-50',
    textColor: dk.isDark ? 'text-white' : 'text-slate-800',
    buttonColor: rt.bgDark
  }, {
    title: 'Genre Spotlights',
    description: 'Deep dives into Romance, Fantasy, Sci-Fi, Mystery, and Thriller.',
    icon: <HeartIcon className="h-6 w-6 text-pink-500" data-id="element-15727" data-mp="w1rjvd1tcraw" />,
    linkTo: '/trove/genres',
    bgColor: dk.isDark ? 'bg-gray-800' : 'bg-pink-50',
    textColor: dk.isDark ? 'text-white' : 'text-pink-900',
    buttonColor: 'bg-pink-500 hover:bg-pink-600'
  }, {
    title: 'Audio & Narration',
    description: 'Resources, interviews, and insights for audiobook creators and fans.',
    icon: <HeadphonesIcon className={`h-6 w-6 ${rt.textPrimary}`} data-id="element-15728" data-mp="2xtgzmaii4w9c" />,
    linkTo: '/trove/audio',
    bgColor: dk.isDark ? 'bg-gray-800' : rt.bgLight,
    textColor: dk.isDark ? 'text-white' : rt.textPrimary,
    buttonColor: rt.bgDark
  }, {
    title: 'Industry Events',
    description: 'Conventions, signings, book clubs, and literary gatherings.',
    icon: <CalendarIcon className="h-6 w-6 text-green-500" data-id="element-15729" data-mp="3gsuxh3jcqr54" />,
    linkTo: '/trove/events',
    bgColor: dk.isDark ? 'bg-gray-800' : 'bg-green-50',
    textColor: dk.isDark ? 'text-white' : 'text-green-900',
    buttonColor: 'bg-green-600 hover:bg-green-700'
  }, isReader ? {
    title: 'Story in Your Head',
    description: 'Creative prompts, writing inspiration, and storytelling tools for readers who write.',
    icon: <PenToolIcon className="h-6 w-6 text-amber-500" data-id="element-15730" data-mp="3e83uxdnbqof" />,
    linkTo: '/trove/story-in-your-head',
    bgColor: dk.isDark ? 'bg-gray-800' : 'bg-amber-50',
    textColor: dk.isDark ? 'text-white' : 'text-amber-900',
    buttonColor: 'bg-amber-500 hover:bg-amber-600'
  } : {
    title: 'Services Hub',
    description: 'Find editors, cover designers, formatters, and other service providers with open availability.',
    icon: <WrenchIcon className="h-6 w-6 text-purple-500" data-id="element-15731" data-mp="d09f2lb2014a" />,
    linkTo: '/trove/services',
    bgColor: dk.isDark ? 'bg-gray-800' : 'bg-purple-50',
    textColor: dk.isDark ? 'text-white' : 'text-purple-900',
    buttonColor: 'bg-purple-600 hover:bg-purple-700'
  }, ...(!isReader ? [{
    title: 'Author Launchpad',
    description: 'Publishing roadmaps, vendor tools, and resources to launch your book.',
    icon: <BriefcaseIcon className={`h-6 w-6 ${rt.textPrimary}`} data-id="element-15732" data-mp="3chz5rrnd9tnq" />,
    linkTo: '/trove/author-launchpad',
    bgColor: dk.isDark ? 'bg-gray-800' : rt.bgLight,
    textColor: dk.isDark ? 'text-white' : rt.textPrimary,
    buttonColor: rt.bgDark
  }] : [])];
  return <TroveLayout data-id="element-15733" data-mp="16zmuv50f3zpi">
      {/* Header */}
      <div className="mb-8" data-id="element-15734" data-mp="t2gefh362d3g">
        <h1 className={`text-3xl sm:text-4xl font-bold mb-2 ${dk.textPrimary}`} data-id="element-15735" data-mp="t39oos1c3kqc">
          The Trove
        </h1>
        <p className={`text-base mb-6 ${dk.textMuted}`} data-id="element-15736" data-mp="x41wrhygzjl1">
          {isReader ? 'Discover new releases, cover reveals, and the latest in indie publishing' : 'Industry updates, collaboration opportunities, and vendor resources'}
        </p>
        <div className="max-w-md" data-id="element-15737" data-mp="1c1m0ymnjlaq5">
          <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} placeholder="Search articles, topics, and authors..." variant="pill" suggestions={[]} data-id="element-15738" data-mp="1c0au2dysvmut" />
        </div>
      </div>

      <div className="space-y-10" data-id="element-15739" data-mp="28iyq341osuy7">
        {/* Role Indicator */}

        {/* Featured Article — shown first */}
        <section data-id="element-15740" data-mp="3t82k2ov0l4zs">
          <h2 className={`text-xl font-semibold mb-4 ${dk.textPrimary}`} data-id="element-15741" data-mp="3t7j77mk816sm">
            Featured Article
          </h2>
          <article className={`${dk.cardSubtle} rounded-lg shadow-sm border overflow-hidden`} data-id="element-15742" data-mp="3o7y89c51wyfw">
            <div className="md:flex" data-id="element-15743" data-mp="1ugcc4zf9816">
              <div className="md:w-2/5" data-id="element-15744" data-mp="1vho6bo9hyjy">
                <img src={featuredArticles[0].image} alt={featuredArticles[0].title} className="w-full h-48 md:h-full object-cover" data-id="element-15745" data-mp="1wso20el6m5s" />
              </div>
              <div className="md:w-3/5 p-6" data-id="element-15746" data-mp="3k86193vb30cn">
                <div className="flex items-center mb-3" data-id="element-15747" data-mp="3ka0mkjahouh9">
                  <span className={`px-2.5 py-0.5 text-white text-xs font-medium ${rt.badgeRadius} ${rt.badgeBg}`} data-id="element-15748" data-mp="3f8te0i0ivr9j">
                    {featuredArticles[0].category}
                  </span>
                  <span className={`ml-3 text-xs ${dk.textMuted}`} data-id="element-15749" data-mp="uic20pqkpzog">
                    {featuredArticles[0].date}
                  </span>
                </div>
                <h3 className={`text-xl font-bold mb-2 ${dk.textPrimary}`} data-id="element-15750" data-mp="16l515xwrsead">
                  {featuredArticles[0].title}
                </h3>
                <p className={`text-sm mb-4 ${dk.textSecondary}`} data-id="element-15751" data-mp="1blsx8xzuvlbg">
                  {featuredArticles[0].excerpt}
                </p>
                <div className="flex items-center justify-between" data-id="element-15752" data-mp="1gh8b3pxv5h7v">
                  <div className="text-xs" data-id="element-15753" data-mp="ixdpmwj205a5">
                    <p className={`font-medium ${dk.textHeading}`} data-id="element-15754" data-mp="nunm2w4v1h7h">
                      {featuredArticles[0].author}
                    </p>
                    <p className={dk.textMuted} data-id="element-15755" data-mp="st8bs1mhkiho">
                      {featuredArticles[0].readTime}
                    </p>
                  </div>
                  <button className={`text-sm font-medium ${rt.accentLink}`} data-id="element-15756" data-mp="5cfgy0twslf">
                    Read full article →
                  </button>
                </div>
              </div>
            </div>
          </article>
        </section>

        {/* Genre Highlights — second */}
        <section data-id="element-15757" data-mp="2icl1hd9xkqvs">
          <div className="flex items-center justify-between mb-4" data-id="element-15758" data-mp="2ed3vmiqldrq3">
            <div className="flex items-center" data-id="element-15759" data-mp="2edvfiwatu10j">
              <HeartIcon className="h-5 w-5 text-pink-500 mr-2" data-id="element-15760" data-mp="29dhi086uq9gr" />
              <h2 className={`text-xl font-semibold ${dk.textPrimary}`} data-id="element-15761" data-mp="29ftz6j4scfsr">
                Romance Highlights
              </h2>
            </div>
            <Link to="/trove/romance" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-15762" data-mp="1zg370o3p6cvu">
              View all
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-15763" data-mp="1qmzuqj4sg62s">
            {romanceArticles.map(article => <ArticleCard key={article.id} article={article} data-id="element-15764" data-mp="1lphhigjqn4oz" />)}
          </div>
        </section>

        {/* Activity Feed — after articles */}
        <TroveActivityFeed posts={activityPosts} title={activityTitle} showTypeFilter={true} data-id="element-15765" data-mp="1bq8qnwk6666u" />

        {/* Trove Sections Grid */}
        <section data-id="element-15766" data-mp="3bt9xszgcoei6">
          <h2 className={`text-xl font-semibold mb-4 ${dk.textPrimary}`} data-id="element-15767" data-mp="3buujdzkosn3w">
            Explore the Trove
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-15768" data-mp="322yhedfhccue">
            {troveSections.map(section => <TroveFeatureCard key={section.title} title={section.title} description={section.description} icon={section.icon} linkTo={section.linkTo} bgColor={section.bgColor} textColor={section.textColor} buttonColor={section.buttonColor} data-id="element-15769" data-mp="370qcfgbf8f2t" />)}
          </div>
        </section>

        {/* Awards Section — teaser */}
        <section className={`p-6 rounded-xl border ${dk.card}`} data-id="element-15770" data-mp="hby2aakji1fc">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-15771" data-mp="had5eear66eb">
            <div className="flex items-start gap-4" data-id="element-15772" data-mp="m96nkk0zilkt">
              <div className={`p-2.5 rounded-xl shrink-0 ${dk.isDark ? 'bg-amber-500/20' : 'bg-amber-100'}`} data-id="element-15773" data-mp="26umrjfo2srcf">
                <TrophyIcon className="h-6 w-6 text-amber-500" data-id="element-15774" data-mp="21snpc942o0h2" />
              </div>
              <div data-id="element-15775" data-mp="2gkplpj6xobcc">
                <h2 className={`text-lg font-bold mb-1 ${dk.textPrimary}`} data-id="element-15776" data-mp="2gl7n0u8gdfr4">
                  Annual Indie Lit Awards 2023
                </h2>
                <p className={`text-sm ${dk.textMuted}`} data-id="element-15777" data-mp="2pj1kw9lv8hge">
                  Celebrating excellence in independent publishing. Voting
                  closes November 30th — 21 days left!
                </p>
                <div className="flex flex-wrap gap-3 mt-3" data-id="element-15778" data-mp="2zgxugwz5n4ce">
                  {awardCategories.map(cat => <span key={cat.id} className={`text-xs px-2.5 py-1 ${rt.badgeRadius} font-medium ${dk.badgeNeutral}`} data-id="element-15779" data-mp="2ujp8smessi6e">
                      {cat.title}
                    </span>)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0" data-id="element-15780" data-mp="309qu3eg09020">
              <div className={`flex items-center text-amber-600 text-sm font-medium`} data-id="element-15781" data-mp="357abgn801sy6">
                <CalendarIcon className="h-4 w-4 mr-1" data-id="element-15782" data-mp="396ri3jz5ww4x" />
                21 days left
              </div>
              <Link to="/trove/awards" className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors" data-id="element-15783" data-mp="3e52qdq9t6yl1">
                Vote Now →
              </Link>
            </div>
          </div>

          {/* Mini nominee preview — top nominee per category */}
          <div className={`mt-5 pt-5 border-t ${dk.divider} grid grid-cols-1 md:grid-cols-3 gap-3`} data-id="element-15784" data-mp="qzccu6a6af7">
            {awardCategories.slice(0, 3).map(category => {
            const leader = [...category.nominees].sort((a, b) => b.votes - a.votes)[0];
            return <div key={category.id} className={`${dk.surfaceMuted} rounded-lg p-3 border ${dk.borderSubtle}`} data-id="element-15785" data-mp="ksaodzsk9fu4">
                  <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${dk.textMuted}`} data-id="element-15786" data-mp="awz8he5f22ks">
                    {category.title}
                  </p>
                  <p className={`text-sm font-medium ${dk.textHeading}`} data-id="element-15787" data-mp="13jk1ej1jimqg">
                    {leader.name}
                  </p>
                  <p className={`text-xs ${dk.textMuted}`} data-id="element-15788" data-mp="ynv4yokthlyd">
                    {'author' in leader && leader.author ? `by ${leader.author}` : leader.work}
                  </p>
                  <div className="flex items-center justify-between mt-2" data-id="element-15789" data-mp="1mb4esdt3qoja">
                    <span className={`text-xs ${dk.textFaint}`} data-id="element-15790" data-mp="1mdh276hmpr3q">
                      {leader.votes.toLocaleString()} votes
                    </span>
                    <button className={`px-2 py-0.5 text-xs rounded-full ${rt.voteButton}`} data-id="element-15791" data-mp="217n99ies6xh0">
                      <ThumbsUpIcon className="h-3 w-3 inline mr-1" data-id="element-15792" data-mp="261hqmf6wz0cx" />
                      {isReader ? 'Vote' : 'Submit'}
                    </button>
                  </div>
                </div>;
          })}
          </div>
        </section>
      </div>
    </TroveLayout>;
};
export default Trove;